import { useCallback, useEffect, useRef, useState } from "react";
import { CircleAlert, Clock3, CloudRain, Filter, Loader2, RefreshCw, RotateCcw, ServerCog, TriangleAlert } from "lucide-react";
import { PageHeader, Panel, Overline } from "@/components/Panel";
import { getMildewPresentation, getMildewReasonLabel } from "@/components/MildewStatus";
import {
  getAdminMildewAlerts,
  getAdminMildewEvaluations,
  getAdminMildewWeather,
  getAdminMildewWorker,
  getAdminTenantFields,
  getAdminTenants,
} from "@/api/treeplet";

const inputClass = "w-full h-10 rounded-md border border-line bg-surface px-3 text-sm outline-none focus:border-brand disabled:cursor-not-allowed disabled:bg-page";
const labelClass = "block text-[10px] font-semibold uppercase tracking-wider text-ink-secondary mb-1";
const INITIAL_FILTERS = { tenantId: "", fieldId: "", deviceId: "", state: "", from: "", to: "" };

const emptyData = () => ({
  evaluations: { data: [], next_cursor: null },
  alerts: { data: [], next_cursor: null },
  weather: { data: [], next_cursor: null },
  worker: null,
});

const formatTime = (value) => value
  ? new Intl.DateTimeFormat("es-ES", { dateStyle: "short", timeStyle: "short" }).format(new Date(value))
  : "Sin datos";

const formatValue = (value, suffix = "") => value == null ? "—" : `${Number(value).toFixed(1)}${suffix}`;

const shortId = (value) => value?.length > 16 ? `…${value.slice(-12)}` : value || "—";

const errorMessage = (error) => {
  if (typeof error?.detail === "string") return error.detail;
  return error?.message || "No se pudo cargar este diagnóstico.";
};

const freshnessLabel = (value) => ({
  CURRENT: "Actual",
  STALE: "Antiguo",
  MISSING: "Sin dato",
}[value] || "Sin confirmar");

const workerState = (failures, errorCode) => {
  if (errorCode || failures > 0) return "Con reintento o fallo";
  return "Sin fallos registrados";
};

const toUtcIso = (value) => {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.valueOf()) ? undefined : parsed.toISOString();
};

const historyFilters = (filters, { includeDevice = true, includeState = false } = {}) => ({
  tenant_id: filters.tenantId || undefined,
  field_id: filters.fieldId || undefined,
  device_id: includeDevice ? filters.deviceId || undefined : undefined,
  state: includeState ? filters.state || undefined : undefined,
  from: toUtcIso(filters.from),
  to: toUtcIso(filters.to),
  limit: 50,
});

function StateBadge({ state }) {
  const presentation = getMildewPresentation({ state, evaluation_status: "CURRENT" });
  const Icon = presentation.icon;
  return <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${presentation.className}`}><Icon size={14} aria-hidden="true" />{presentation.label}</span>;
}

function DiagnosticPanel({ title, subtitle, icon: Icon, loading, error, children, testId }) {
  return (
    <Panel className="min-w-0 overflow-hidden" testId={testId}>
      <div className="flex items-start gap-3 border-b border-line px-4 py-3">
        <div className="rounded-md bg-brand/10 p-2 text-brand"><Icon size={18} aria-hidden="true" /></div>
        <div className="min-w-0"><h2 className="font-heading font-bold text-base text-ink">{title}</h2><p className="mt-0.5 text-xs text-ink-secondary">{subtitle}</p></div>
      </div>
      {loading ? <div className="flex min-h-32 items-center justify-center gap-2 px-4 py-8 text-sm text-ink-tertiary"><Loader2 size={18} className="animate-spin text-brand" /> Cargando…</div>
        : error ? <div className="flex min-h-32 items-center gap-2 px-4 py-6 text-sm text-critical"><CircleAlert size={18} className="shrink-0" />{error}</div>
          : children}
    </Panel>
  );
}

function LoadMore({ cursor, loading, onClick, label = "Cargar más" }) {
  if (!cursor) return null;
  return <div className="border-t border-line px-4 py-3"><button type="button" onClick={onClick} disabled={loading} className="inline-flex items-center gap-2 text-sm font-semibold text-brand disabled:opacity-60">{loading && <Loader2 size={15} className="animate-spin" />}{label}</button></div>;
}

function ReasonList({ reasons = [], empty = "Sin motivo adicional" }) {
  if (!reasons.length) return <span className="text-ink-tertiary">{empty}</span>;
  return <div className="space-y-1">{reasons.map((reason) => <div key={reason}>{getMildewReasonLabel(reason)}</div>)}</div>;
}

function EvaluationsPanel({ envelope, loading, error, onLoadMore, loadingMore }) {
  const data = envelope.data || [];
  return (
    <DiagnosticPanel title="Evaluaciones recientes" subtitle="Transiciones, evidencia y frescura de las entradas" icon={Clock3} loading={loading} error={error} testId="admin-mildew-evaluations">
      {!data.length ? <div className="px-4 py-8 text-sm text-ink-tertiary">No hay evaluaciones para los filtros seleccionados.</div> : <div className="overflow-x-auto tp-scroll">
        <table className="w-full min-w-[1120px] text-left text-xs">
          <thead className="border-b border-line bg-page text-[10px] uppercase tracking-wide text-ink-tertiary"><tr><th className="px-4 py-3 font-semibold">Momento y ámbito</th><th className="px-4 py-3 font-semibold">Estado</th><th className="px-4 py-3 font-semibold">Modelo y entradas</th><th className="px-4 py-3 font-semibold">Motivos y correlación</th><th className="px-4 py-3 font-semibold">Frescura</th></tr></thead>
          <tbody className="divide-y divide-line">
            {data.map((evaluation) => {
              const incompletePair = evaluation.correlation_outcomes?.includes("incomplete_packet_pair");
              return <tr key={evaluation.id} className="align-top">
                <td className="px-4 py-3"><div className="font-semibold text-ink">{formatTime(evaluation.processed_at)}</div><div className="mt-1 text-ink-secondary">{evaluation.tenant_name} · {evaluation.field_name}</div><div className="mt-1 font-mono text-[10px] text-ink-tertiary" title={evaluation.device_id}>{shortId(evaluation.device_id)}</div></td>
                <td className="px-4 py-3"><StateBadge state={evaluation.new_state} /><div className="mt-1 text-ink-secondary">{evaluation.previous_state || "Sin estado previo"} → {evaluation.new_state}</div><div className="mt-1 text-ink-tertiary">{evaluation.state_transition ? "Hubo transición" : "Sin cambio de estado"}</div></td>
                <td className="px-4 py-3"><div className="font-medium text-ink">{evaluation.model_version || "Modelo no informado"}</div><div className="mt-1 text-ink-secondary">{evaluation.input_pair_count} pareja{evaluation.input_pair_count === 1 ? "" : "s"} completa{evaluation.input_pair_count === 1 ? "" : "s"}</div><div className="mt-1 text-ink-secondary">Lluvia 24 h: {formatValue(evaluation.rainfall_24h_mm, " mm")}</div>{evaluation.qualifying_duration_seconds != null && <div className="mt-1 text-ink-secondary">Duración: {Math.round(evaluation.qualifying_duration_seconds / 60)} min</div>}</td>
                <td className="max-w-[290px] px-4 py-3 leading-5 text-ink-secondary"><ReasonList reasons={evaluation.reason_codes} />{evaluation.correlation_outcomes?.length > 0 && <div className="mt-2 border-t border-line pt-2"><span className="font-semibold text-ink">{incompletePair ? "Pareja incompleta" : "Correlación"}: </span>{evaluation.correlation_outcomes.join(", ")}</div>}{evaluation.error_code && <div className="mt-2 text-critical">Error: {evaluation.error_code}</div>}</td>
                <td className="px-4 py-3 text-ink-secondary"><div><span className="font-semibold text-ink">Telemetría: </span>{freshnessLabel(evaluation.telemetry_freshness)}</div><div className="mt-1">{formatTime(evaluation.telemetry_observed_at)}</div><div className="mt-3"><span className="font-semibold text-ink">Meteorología: </span>{freshnessLabel(evaluation.weather_freshness)}</div><div className="mt-1">{formatTime(evaluation.weather_observed_at)}</div>{evaluation.field_alert_id && <div className="mt-2 text-critical">Alerta #{evaluation.field_alert_id} activa</div>}</td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>}
      <LoadMore cursor={envelope.next_cursor} loading={loadingMore} onClick={onLoadMore} />
    </DiagnosticPanel>
  );
}

function AlertsPanel({ envelope, loading, error, onLoadMore, loadingMore }) {
  const data = envelope.data || [];
  return (
    <DiagnosticPanel title="Alertas abiertas" subtitle="Alertas de campo que siguen activas" icon={TriangleAlert} loading={loading} error={error} testId="admin-mildew-alerts">
      {!data.length ? <div className="px-4 py-8 text-sm text-ink-tertiary">No hay alertas abiertas para los filtros seleccionados.</div> : <div className="divide-y divide-line">
        {data.map((alert) => <article key={alert.id} className="px-4 py-3 text-sm">
          <div className="flex items-start justify-between gap-3"><div className="min-w-0"><div className="font-semibold text-ink">{alert.field_name}</div><div className="mt-0.5 text-xs text-ink-secondary">{alert.tenant_name} · abierta {formatTime(alert.opened_at)}</div></div><span className="shrink-0 text-xs font-semibold text-critical">Abierta</span></div>
          <div className="mt-2 text-xs text-ink-secondary">{alert.affected_device_ids.length} dispositivo{alert.affected_device_ids.length === 1 ? "" : "s"} responsable{alert.affected_device_ids.length === 1 ? "" : "s"} · {alert.model_version || "Modelo no informado"}</div>
          <div className="mt-2 text-xs leading-5 text-ink-secondary"><ReasonList reasons={alert.reason_codes} /></div>
        </article>)}
      </div>}
      <LoadMore cursor={envelope.next_cursor} loading={loadingMore} onClick={onLoadMore} />
    </DiagnosticPanel>
  );
}

function QueueCard({ label, value, loading }) {
  return <div className="rounded-md border border-line bg-page px-3 py-2"><div className="text-[10px] font-semibold uppercase tracking-wide text-ink-tertiary">{label}</div><div className="mt-1 font-heading text-2xl font-extrabold text-ink">{loading ? "…" : value ?? "—"}</div></div>;
}

function WorkerPanel({ data, loading, error, onLoadMore, loadingMore }) {
  const queue = data?.queue;
  return (
    <DiagnosticPanel title="Trabajador y reintentos" subtitle="Colas pendientes, checkpoints y actualización meteorológica" icon={ServerCog} loading={loading} error={error} testId="admin-mildew-worker">
      <div className="grid grid-cols-2 gap-2 border-b border-line p-4 lg:grid-cols-4">
        <QueueCard label="Eval. vencidas" value={queue?.due_device_evaluations} loading={!data} />
        <QueueCard label="Eval. reintentando" value={queue?.retrying_device_evaluations} loading={!data} />
        <QueueCard label="Tiempo vencido" value={queue?.due_weather_refreshes} loading={!data} />
        <QueueCard label="Tiempo reintentando" value={queue?.retrying_weather_refreshes} loading={!data} />
      </div>
      <div className="grid gap-0 lg:grid-cols-2">
        <section className="border-b border-line p-4 lg:border-b-0 lg:border-r"><Overline>Checkpoints globales</Overline>{!data?.checkpoints?.length ? <p className="mt-3 text-sm text-ink-tertiary">No hay checkpoints registrados.</p> : <div className="mt-3 space-y-3">{data.checkpoints.map((checkpoint) => <div key={checkpoint.job_key} className="rounded-md bg-page p-3 text-xs"><div className="font-semibold text-ink">{checkpoint.job_key}</div><div className="mt-1 text-ink-secondary">{workerState(checkpoint.consecutive_failures, checkpoint.last_error_code)}</div><div className="mt-1 text-ink-tertiary">Éxito: {formatTime(checkpoint.last_success_at)} · Próximo: {formatTime(checkpoint.next_due_at)}</div>{checkpoint.last_error_code && <div className="mt-1 text-critical">{checkpoint.last_error_code}</div>}</div>)}</div>}</section>
        <section className="p-4"><Overline>Actualizaciones por parcela</Overline>{!data?.weather_refreshes?.length ? <p className="mt-3 text-sm text-ink-tertiary">No hay estado meteorológico para los filtros seleccionados.</p> : <div className="mt-3 space-y-3">{data.weather_refreshes.map((refresh) => <div key={refresh.field_id} className="rounded-md bg-page p-3 text-xs"><div className="font-semibold text-ink">{refresh.field_name} <span className="font-normal text-ink-tertiary">· {refresh.tenant_name}</span></div><div className="mt-1 text-ink-secondary">{workerState(refresh.consecutive_failures, refresh.last_error_code)}</div><div className="mt-1 text-ink-tertiary">Éxito: {formatTime(refresh.last_success_at)} · Reintento: {formatTime(refresh.retry_after_at)}</div>{refresh.last_error_code && <div className="mt-1 text-critical">{refresh.last_error_code}</div>}</div>)}</div>}</section>
      </div>
      <LoadMore cursor={data?.next_cursor} loading={loadingMore} onClick={onLoadMore} label="Cargar más estados meteorológicos" />
    </DiagnosticPanel>
  );
}

function WeatherPanel({ envelope, loading, error, onLoadMore, loadingMore }) {
  const data = envelope.data || [];
  return (
    <DiagnosticPanel title="Observaciones meteorológicas" subtitle="Datos normalizados y estado de actualización por parcela" icon={CloudRain} loading={loading} error={error} testId="admin-mildew-weather">
      {!data.length ? <div className="px-4 py-8 text-sm text-ink-tertiary">No hay observaciones meteorológicas para los filtros seleccionados.</div> : <div className="overflow-x-auto tp-scroll">
        <table className="w-full min-w-[920px] text-left text-xs"><thead className="border-b border-line bg-page text-[10px] uppercase tracking-wide text-ink-tertiary"><tr><th className="px-4 py-3 font-semibold">Parcela y fuente</th><th className="px-4 py-3 font-semibold">Observación</th><th className="px-4 py-3 font-semibold">Precipitación y calidad</th><th className="px-4 py-3 font-semibold">Actualización</th></tr></thead><tbody className="divide-y divide-line">{data.map((weather) => <tr key={weather.id} className="align-top"><td className="px-4 py-3"><div className="font-semibold text-ink">{weather.field_name}</div><div className="mt-1 text-ink-secondary">{weather.tenant_name} · {weather.provider}</div><div className="mt-1 text-ink-tertiary">{weather.source_kind} · {weather.source_key}</div></td><td className="px-4 py-3 text-ink-secondary"><div>{formatTime(weather.observed_start_at)} –</div><div>{formatTime(weather.observed_end_at)}</div><div className="mt-1 text-ink-tertiary">Recibido: {formatTime(weather.fetched_at)}</div></td><td className="px-4 py-3 text-ink-secondary"><div className="font-semibold text-ink">{formatValue(weather.precipitation_mm, " mm")}</div><div className="mt-1">Calidad: {weather.quality_status}</div><div className="mt-1">Coste de consulta: {weather.query_cost ?? "—"}</div></td><td className="px-4 py-3 text-ink-secondary"><div>{workerState(weather.consecutive_failures, weather.last_error_code)}</div><div className="mt-1">Éxito: {formatTime(weather.last_success_at)}</div><div className="mt-1">Reintento: {formatTime(weather.retry_after_at)}</div>{weather.last_error_code && <div className="mt-1 text-critical">{weather.last_error_code}</div>}</td></tr>)}</tbody></table>
      </div>}
      <LoadMore cursor={envelope.next_cursor} loading={loadingMore} onClick={onLoadMore} />
    </DiagnosticPanel>
  );
}

export default function AdminMildewDiagnostics() {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
  const [tenants, setTenants] = useState([]);
  const [fields, setFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [data, setData] = useState(emptyData);
  const [loading, setLoading] = useState({ evaluations: true, alerts: true, weather: true, worker: true });
  const [loadingMore, setLoadingMore] = useState({ evaluations: false, alerts: false, weather: false, worker: false });
  const [errors, setErrors] = useState({ evaluations: "", alerts: "", weather: "", worker: "" });
  const sequence = useRef(0);

  const loadAll = useCallback(async (activeFilters) => {
    const requestId = ++sequence.current;
    setData(emptyData());
    setErrors({ evaluations: "", alerts: "", weather: "", worker: "" });
    setLoading({ evaluations: true, alerts: true, weather: true, worker: true });
    const evaluationRequest = historyFilters(activeFilters, { includeState: true });
    const alertRequest = { ...historyFilters(activeFilters), status: "OPEN" };
    const weatherRequest = historyFilters(activeFilters, { includeDevice: false });
    const workerRequest = {
      tenant_id: activeFilters.tenantId || undefined,
      field_id: activeFilters.fieldId || undefined,
      device_id: activeFilters.deviceId || undefined,
      limit: 50,
    };
    const responses = await Promise.allSettled([
      getAdminMildewEvaluations(evaluationRequest),
      getAdminMildewAlerts(alertRequest),
      getAdminMildewWeather(weatherRequest),
      getAdminMildewWorker(workerRequest),
    ]);
    if (requestId !== sequence.current) return;
    setData({
      evaluations: responses[0].status === "fulfilled" ? responses[0].value : { data: [], next_cursor: null },
      alerts: responses[1].status === "fulfilled" ? responses[1].value : { data: [], next_cursor: null },
      weather: responses[2].status === "fulfilled" ? responses[2].value : { data: [], next_cursor: null },
      worker: responses[3].status === "fulfilled" ? responses[3].value.data : null,
    });
    setErrors({
      evaluations: responses[0].status === "rejected" ? errorMessage(responses[0].reason) : "",
      alerts: responses[1].status === "rejected" ? errorMessage(responses[1].reason) : "",
      weather: responses[2].status === "rejected" ? errorMessage(responses[2].reason) : "",
      worker: responses[3].status === "rejected" ? errorMessage(responses[3].reason) : "",
    });
    setLoading({ evaluations: false, alerts: false, weather: false, worker: false });
  }, []);

  useEffect(() => {
    void loadAll(appliedFilters);
  }, [appliedFilters, loadAll]);

  useEffect(() => {
    let active = true;
    getAdminTenants()
      .then((envelope) => active && setTenants(envelope.data || []))
      .catch((requestError) => active && setLookupError(errorMessage(requestError)));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    if (!filters.tenantId) {
      setFields([]);
      setFieldsLoading(false);
      return () => { active = false; };
    }
    setFieldsLoading(true);
    setLookupError("");
    getAdminTenantFields(filters.tenantId)
      .then((envelope) => active && setFields(envelope.data || []))
      .catch((requestError) => active && setLookupError(errorMessage(requestError)))
      .finally(() => active && setFieldsLoading(false));
    return () => { active = false; };
  }, [filters.tenantId]);

  const loadMore = useCallback(async (key) => {
    const cursor = key === "worker" ? data.worker?.next_cursor : data[key]?.next_cursor;
    if (!cursor) return;
    const requestId = sequence.current;
    setLoadingMore((current) => ({ ...current, [key]: true }));
    try {
      const request = key === "evaluations"
        ? getAdminMildewEvaluations({ ...historyFilters(appliedFilters, { includeState: true }), next_cursor: cursor })
        : key === "alerts"
          ? getAdminMildewAlerts({ ...historyFilters(appliedFilters), status: "OPEN", next_cursor: cursor })
          : key === "weather"
            ? getAdminMildewWeather({ ...historyFilters(appliedFilters, { includeDevice: false }), next_cursor: cursor })
            : getAdminMildewWorker({ tenant_id: appliedFilters.tenantId || undefined, field_id: appliedFilters.fieldId || undefined, device_id: appliedFilters.deviceId || undefined, limit: 50, next_cursor: cursor });
      const response = await request;
      if (requestId !== sequence.current) return;
      setData((current) => key === "worker"
        ? { ...current, worker: { ...response.data, weather_refreshes: [...(current.worker?.weather_refreshes || []), ...(response.data.weather_refreshes || [])] } }
        : { ...current, [key]: { ...response, data: [...(current[key]?.data || []), ...(response.data || [])] } });
      setErrors((current) => ({ ...current, [key]: "" }));
    } catch (requestError) {
      if (requestId === sequence.current) setErrors((current) => ({ ...current, [key]: errorMessage(requestError) }));
    } finally {
      if (requestId === sequence.current) setLoadingMore((current) => ({ ...current, [key]: false }));
    }
  }, [appliedFilters, data]);

  const updateFilter = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value, ...(name === "tenantId" ? { fieldId: "" } : {}) }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setAppliedFilters({ ...filters });
  };

  const clearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
  };

  return (
    <div className="h-full min-h-0 overflow-y-auto tp-scroll max-w-[1600px] mx-auto w-full animate-fade-in pr-1">
      <PageHeader title="Diagnóstico de mildiu" subtitle="Vista técnica para plataforma: estado, historial y trabajo pendiente" testId="admin-mildew-header" actions={<button type="button" onClick={() => void loadAll(appliedFilters)} className="inline-flex items-center gap-2 text-sm font-semibold text-brand"><RefreshCw size={15} />Actualizar</button>} />
      <Panel className="mb-4 p-4" testId="admin-mildew-filters">
        <div className="flex items-start gap-2"><Filter size={17} className="mt-0.5 text-brand" aria-hidden="true" /><div><Overline>Filtros del servidor</Overline><p className="mt-1 text-xs text-ink-secondary">Los filtros se aplican a los historiales y a las colas que admiten ese ámbito. Las alertas se mantienen en estado abierto.</p></div></div>
        {lookupError && <div className="mt-3 rounded-md border border-critical/30 bg-critical/5 px-3 py-2 text-xs text-critical">No se pudieron cargar los selectores: {lookupError}</div>}
        <form onSubmit={applyFilters} className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
          <label><span className={labelClass}>Cliente / tenant</span><select name="tenantId" value={filters.tenantId} onChange={updateFilter} className={inputClass}><option value="">Todos los clientes</option>{tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.name}</option>)}</select></label>
          <label><span className={labelClass}>Parcela</span><select name="fieldId" value={filters.fieldId} onChange={updateFilter} className={inputClass} disabled={!filters.tenantId || fieldsLoading}><option value="">{fieldsLoading ? "Cargando parcelas…" : "Todas las parcelas"}</option>{fields.map((field) => <option key={field.id} value={field.id}>{field.name}</option>)}</select></label>
          <label><span className={labelClass}>Dispositivo</span><input name="deviceId" value={filters.deviceId} onChange={updateFilter} className={inputClass} placeholder="ID exacto" /></label>
          <label><span className={labelClass}>Estado de mildiu</span><select name="state" value={filters.state} onChange={updateFilter} className={inputClass}><option value="">Todos los estados</option><option value="NO_RISK">Sin riesgo</option><option value="RISK">Riesgo</option><option value="UNKNOWN">Datos insuficientes</option></select></label>
          <label><span className={labelClass}>Desde</span><input type="datetime-local" name="from" value={filters.from} onChange={updateFilter} className={inputClass} /></label>
          <label><span className={labelClass}>Hasta</span><input type="datetime-local" name="to" value={filters.to} onChange={updateFilter} className={inputClass} /></label>
          <div className="flex items-end gap-3 xl:col-span-6"><button className="h-10 rounded-md bg-brand px-4 text-sm font-semibold text-white">Aplicar filtros</button><button type="button" onClick={clearFilters} className="inline-flex h-10 items-center gap-2 rounded-md border border-line px-4 text-sm font-semibold text-ink-secondary"><RotateCcw size={15} />Limpiar</button></div>
        </form>
      </Panel>

      <div className="grid gap-4 pb-6 xl:grid-cols-3">
        <div className="xl:col-span-3"><EvaluationsPanel envelope={data.evaluations} loading={loading.evaluations} error={errors.evaluations} onLoadMore={() => void loadMore("evaluations")} loadingMore={loadingMore.evaluations} /></div>
        <div><AlertsPanel envelope={data.alerts} loading={loading.alerts} error={errors.alerts} onLoadMore={() => void loadMore("alerts")} loadingMore={loadingMore.alerts} /></div>
        <div className="xl:col-span-2"><WorkerPanel data={data.worker} loading={loading.worker} error={errors.worker} onLoadMore={() => void loadMore("worker")} loadingMore={loadingMore.worker} /></div>
        <div className="xl:col-span-3"><WeatherPanel envelope={data.weather} loading={loading.weather} error={errors.weather} onLoadMore={() => void loadMore("weather")} loadingMore={loadingMore.weather} /></div>
      </div>
    </div>
  );
}
